import React, { Fragment, useState } from 'react'
import { useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { Button } from '../../../../common'
import { Association, Team } from '../../../../types/generated.types'
import ExtendSalesPeriodModal from './ExtendSalesPeriodModal'
import { extendSalesPeriodMutation } from '../../../../queries/team'
import Webbshop from '../../Webbshop'
import { useDashboardTeamContext } from '../../../../context/DashboardTeamContext'

// Configure dayjs to use ISO week
dayjs.extend(isoWeek);

// Helper function to format date range
const formatSalesPeriod = (startDate: string | null | undefined, endDate: string | null | undefined): string => {
  if (!startDate || !endDate) {
    return 'Er säljperiod';
  }

  const startWeek = dayjs(startDate).isoWeek();
  const endWeek = dayjs(endDate).isoWeek();

  return `Er säljperiod v.${startWeek} - v.${endWeek}`;
};

// Helper function to get extended week
const getExtendedWeek = (endDate: string | null | undefined): string => {
  if (!endDate) {
    return 'Förläng försäljningsperioden';
  }

  const extendedWeek = dayjs(endDate).add(7, 'day').isoWeek();

  return `Förläng t.o.m v.${extendedWeek}`;
};

interface TeamShopProps {
  association: Association
  team: Team
  refetchTeam: () => void
}

const TeamShop: React.FC<TeamShopProps> = ({ association, team, refetchTeam }) => {
  const { shopShareDomain } = useDashboardTeamContext();
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [isExtending, setIsExtending] = useState(false);

  const [extendSalesPeriod] = useMutation(extendSalesPeriodMutation, {
    onCompleted: async (data) => {
      console.log('Sales period extended successfully:', data);
      setIsExtending(false);
      setShowExtendModal(false);

      // Refetch the team data to update the UI
      try {
        await refetchTeam();
      } catch (error) {
        console.error('Error refetching team data:', error);
      }
    },
    onError: (error) => {
      console.error('Error extending sales period:', error);
      setIsExtending(false);
      alert('Kunde inte förlänga försäljningsperioden. Försök igen.');
    }
  });

  const handleOpenExtendModal = () => setShowExtendModal(true);
  const handleCloseExtendModal = () => setShowExtendModal(false);

  const handleConfirmExtend = () => {
    setIsExtending(true);
    extendSalesPeriod({
      variables: {
        teamId: team._id,
      }
    });
  };

  return (
    <Fragment>
      <Webbshop
        association={association}
        team={team}
        shopShareDomain={shopShareDomain}
      >
        <p className='mt-lg'>{formatSalesPeriod(team?.salesStartDate, team?.salesEndDate)}</p>
        <Button
          className='secondary'
          onClick={handleOpenExtendModal}
        >
          {getExtendedWeek(team?.salesEndDate)}
        </Button>
      </Webbshop>
      {showExtendModal &&
        <ExtendSalesPeriodModal
          team={team}
          closeModal={handleCloseExtendModal}
          onConfirm={handleConfirmExtend}
          isExtending={isExtending}
        />
      }
    </Fragment>
  )
}

export default TeamShop
